import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import Select, { SingleValue } from 'react-select';
import { useDispatch, useSelector } from 'react-redux';
import { skipToken } from '@reduxjs/toolkit/query';
import { WarningTriangleIcon } from '@iconicicons/react';
import { ChevronDown, ChevronUp, SlidersHorizontal } from 'lucide-react';
import {
  useKPathsMutation,
  useGetContractsQuery,
  useGetPathReportAssetsQuery,
  useSyncPathReportAssetsMutation,
  useGetTSPsQuery,
  useGetRegionsQuery,
} from '../services/edgeApi';
import { pathFinderActions as pfActions } from './pathFinderSlice';
import { plotterActions } from '../plotter/plotterSlice';
import { RootState } from '../store';
import { useEditableNumber } from '../hooks/useEditableNumber';
import { priceFormatter } from '../helpers/formatters';
import { readLastState } from '../utils/pathReportState';
import { PathReportSelectionContext } from '../utils/pathReportState';
import { ScenarioPathsProgressPayload } from '../hooks/useScenarioPaths';
import { selectAllPoints } from '../map/slice/mapSelectors';
import * as filtersSlice from '../filters/filtersSlice';
import { ReduxPointSearch } from '../search/ReduxPointSearch';
import { useLocalStorage } from '../helpers/useLocalStorage';
import { selectStyles } from '../helpers/MultiSelect';
import { useTheme } from '../helpers/hooks';
import {
  scenarioActions,
  selectScenario,
  selectPathFinderRegions,
} from '../scenarios/scenarioSlice';
import { Contract, Region } from '../types';
import {
  ContractPickOption,
  ContractUsageManagerModal,
} from './ContractUsageManagerModal';
import {
  ContractUsageDraft,
  ContractUsageMode,
  ContractUsageWarning,
  contractUsageError,
  findInvalidEnabledContractUsageIndex,
  getContractUsageRequestFields,
  getEnabledContractUsages,
} from './contractUsageDraft';
import { pathServiceErrorMessage } from './pathServiceErrorMessage';

export type PathFinderControlsProps = {
  scenario_id?: string;
  gas_date?: string;
  progress?: ScenarioPathsProgressPayload | null;
};

type AssetPickOption = { value: string; label: string };
export const PathFinderControls: React.FC<PathFinderControlsProps> = (
  props,
) => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: regions } = useGetRegionsQuery(undefined);

  const kPathsData = useSelector(
    (s: RootState) => (s as any).pathFinder?.results ?? [],
  );
  const avoidConstraints = useSelector(
    (s: RootState) => (s as any).pathFinder?.avoidConstraints ?? [],
  );
  const [autoRefreshAvoidConstraints, setAutoRefreshAvoidConstraints] =
    useLocalStorage('path_finder_auto_refresh_avoid_constraints', false);
  const previousAvoidConstraintsKey = React.useRef(
    JSON.stringify(avoidConstraints),
  );
  const allPoints = useSelector((s: RootState) => selectAllPoints(s as any));
  const selectedScenario = useSelector((s: RootState) =>
    selectScenario(s as any),
  );

  const kNum = useEditableNumber(20, { min: 1, max: 100, integer: true });
  const [requireVolume, setRequireVolume] = useState<boolean>(true);
  const minVol = useEditableNumber(1000, { min: 0 });
  const icpNum = useEditableNumber(0.05, { min: 0 });
  const latestRunId = React.useRef<number>(0);
  const [pathMode, setPathMode] = useState<'sets' | 'diverse'>(() =>
    searchParams.get('mode') === 'sets' ? 'sets' : 'diverse',
  );
  const [diversifyOn, setDiversifyOn] = useState<
    'both' | 'receipt' | 'delivery'
  >(() => {
    const v = (searchParams.get('div_on') || 'both').toLowerCase();
    return v === 'receipt' || v === 'delivery' ? v : 'both';
  });
  const diversityPenalty = useEditableNumber(1, { min: 0 });

  const [kPaths, { isLoading: kPathsLoading }] = useKPathsMutation();
  const [syncPathReportAssets] = useSyncPathReportAssetsMutation();
  const selectionCtx = React.useContext(PathReportSelectionContext);
  const lastState = React.useMemo(() => readLastState() || {}, []);

  const hasCtx = Boolean(selectionCtx);
  const scenarioId = hasCtx
    ? selectionCtx?.selected?.scenario_id
    : props.scenario_id ?? lastState.scenario_id ?? 'default';
  const gasDate = hasCtx
    ? selectionCtx?.selected?.gas_date
    : props.gas_date ??
      lastState.gas_date ??
      new Date().toISOString().slice(0, 10);

  const shouldQueryAssets = Boolean(scenarioId && gasDate);
  const { data: assetsResp } = useGetPathReportAssetsQuery(
    shouldQueryAssets
      ? { scenario_id: scenarioId, gas_date: gasDate }
      : skipToken,
    { refetchOnMountOrArgChange: true },
  );
  const { data: tspsResp } = useGetTSPsQuery();

  const [didInitReceipt, setDidInitReceipt] = useState(false);
  const [didInitDelivery, setDidInitDelivery] = useState(false);
  const [didInitTsp, setDidInitTsp] = useState(false);

  const receiptPointOptions = React.useMemo(() => {
    const src = assetsResp?.buy_assets_by_point as
      | Record<string, any[]>
      | undefined;
    if (!src) return [] as { id: number; label: string }[];
    const out: Array<{ id: number; label: string; _priceValue?: number }> = [];
    Object.entries(src).forEach(([pid, assets]) => {
      if (!assets || assets.length === 0) return;
      const first = (assets || [])[0] as any;
      const pool = first?.pool || '';
      const desc = first?.description || '';
      // min buy price for display/sort
      const prices = (assets || [])
        .map((a: any) => Number(a?.base_price ?? a?.price))
        .filter((v) => Number.isFinite(v));
      const basePrice = prices.length ? Math.min(...prices) : null;
      const priceStr = Number.isFinite(Number(basePrice))
        ? priceFormatter.format(Number(basePrice))
        : '';
      const namePart = pool ? ` • ${pool}` : desc ? ` • ${desc}` : '';
      const pricePart = priceStr ? ` — ${priceStr}` : '';
      out.push({
        id: Number(pid),
        label: `#${pid}${namePart}${pricePart}`,
        _priceValue: Number.isFinite(Number(basePrice))
          ? Number(basePrice)
          : Number.POSITIVE_INFINITY,
      });
    });
    out.sort(
      (a, b) => (a._priceValue ?? Infinity) - (b._priceValue ?? Infinity),
    );
    return out.map(({ id, label }) => ({ id, label }));
  }, [assetsResp]);

  const deliveryPointOptions = React.useMemo(() => {
    const src = assetsResp?.sell_assets_by_point as
      | Record<string, any[]>
      | undefined;
    if (!src) return [] as { id: number; label: string }[];
    const out: Array<{ id: number; label: string; _priceValue?: number }> = [];
    Object.entries(src).forEach(([pid, assets]) => {
      if (!assets || assets.length === 0) return;
      const first = (assets || [])[0] as any;
      const pool = first?.pool || '';
      const desc = first?.description || '';
      // max sell price for display/sort
      const prices = (assets || [])
        .map((a: any) => Number(a?.base_price ?? a?.price))
        .filter((v) => Number.isFinite(v));
      const basePrice = prices.length ? Math.max(...prices) : null;
      const priceStr = Number.isFinite(Number(basePrice))
        ? priceFormatter.format(Number(basePrice))
        : '';
      const namePart = pool ? ` • ${pool}` : desc ? ` • ${desc}` : '';
      const pricePart = priceStr ? ` — ${priceStr}` : '';
      out.push({
        id: Number(pid),
        label: `#${pid}${namePart}${pricePart}`,
        _priceValue: Number.isFinite(Number(basePrice))
          ? Number(basePrice)
          : -Infinity,
      });
    });
    out.sort(
      (a, b) => (b._priceValue ?? -Infinity) - (a._priceValue ?? -Infinity),
    );
    return out.map(({ id, label }) => ({ id, label }));
  }, [assetsResp]);

  const [rAssetPoints, setRAssetPoints] = useState<number[]>([]);
  const [dAssetPoints, setDAssetPoints] = useState<number[]>([]);
  const [rFilter, setRFilter] = useState<string>('');
  const [dFilter, setDFilter] = useState<string>('');
  const [tspFilter, setTspFilter] = useState<string>('');
  const [tspIds, setTspIds] = useState<number[]>([]);
  const [isPathingOptionsOpen, setIsPathingOptionsOpen] =
    useState<boolean>(false);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState<boolean>(false);
  const [enforceRegionPathing, setEnforceRegionPathing] =
    useState<boolean>(false);
  const [isConstraintAdvancedOpen, setIsConstraintAdvancedOpen] =
    useState<boolean>(false);
  const [isContractUsageOpen, setIsContractUsageOpen] =
    useState<boolean>(false);
  const [isContractUsageManagerOpen, setIsContractUsageManagerOpen] =
    useState<boolean>(false);
  const [isPathSettingsOpen, setIsPathSettingsOpen] = useState<boolean>(false);
  const [contractUsages, setContractUsages] = useState<ContractUsageDraft[]>(
    [],
  );
  const [contractUsageMode, setContractUsageMode] =
    useState<ContractUsageMode>('enforce');
  const [contractUsageWarnings, setContractUsageWarnings] = useState<
    ContractUsageWarning[]
  >([]);
  const nextContractUsageRowId = React.useRef(1);
  const hasCurrentScenarioContracts =
    selectedScenario?.id != null &&
    String(selectedScenario.id) === String(scenarioId) &&
    (selectedScenario.contracts?.length || 0) > 0;
  const { data: activeContractsResp, isFetching: contractsLoading } =
    useGetContractsQuery(
      {
        date: gasDate,
        include_details: true,
        limit: 10000,
        offset: 0,
        is_active_on_date: true,
        use_in_scenarios: true,
      },
      {
        skip:
          !gasDate ||
          !isContractUsageManagerOpen ||
          hasCurrentScenarioContracts,
      },
    );
  const [showOutsideTspAssets, setShowOutsideTspAssets] =
    useState<boolean>(false);
  const [isTspAdvancedOpen, setIsTspAdvancedOpen] = useState<boolean>(false);
  const [isReceiptAdvancedOpen, setIsReceiptAdvancedOpen] =
    useState<boolean>(false);
  const [isDeliveryAdvancedOpen, setIsDeliveryAdvancedOpen] =
    useState<boolean>(false);
  const [isAddingReceiptNew, setIsAddingReceiptNew] = useState<boolean>(false);
  const [isAddingDeliveryNew, setIsAddingDeliveryNew] =
    useState<boolean>(false);

  const selectedRegions = useSelector(selectPathFinderRegions);

  const setSelectedRegions = useCallback(
    (regions: Region[]) => {
      dispatch(
        scenarioActions.setPathFinderRegions({ pathFinderRegions: regions }),
      );
    },
    [dispatch, selectedRegions],
  );

  useEffect(() => {
    if (!isAdvancedOpen) return;

    setIsPathingOptionsOpen(true);
    setIsTspAdvancedOpen(true);
    setIsConstraintAdvancedOpen(true);
    setIsContractUsageOpen(true);
    setIsPathSettingsOpen(true);
  }, [isAdvancedOpen]);

  const [adhocReceiptPointId, setAdhocReceiptPointId] = useState<number | null>(
    () => {
      const raw = searchParams.get('adhoc_r_point');
      if (!raw) return null;
      const parsed = Number(raw);
      return Number.isFinite(parsed) ? parsed : null;
    },
  );
  const [adhocDeliveryPointId, setAdhocDeliveryPointId] = useState<
    number | null
  >(() => {
    const raw = searchParams.get('adhoc_d_point');
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  });

  const tspOptions = React.useMemo(() => {
    const results = tspsResp || {};
    const out: { id: number; label: string }[] = Object.values(
      results || {},
    ).map((tsp: any) => ({
      id: Number(tsp.id),
      label: tsp.short_name || tsp.name,
    }));
    return out.sort((a, b) => a.label.localeCompare(b.label));
  }, [tspsResp]);

  const contractOptions = React.useMemo<ContractPickOption[]>(() => {
    const scenarioContracts = hasCurrentScenarioContracts
      ? selectedScenario.contracts || []
      : [];
    const source =
      scenarioContracts.length > 0
        ? scenarioContracts
        : (activeContractsResp as Contract[] | undefined) || [];

    return source
      .map((contract) => {
        const id = Number(contract.id);
        if (!Number.isInteger(id) || id <= 0) return null;
        const tsp = contract.tsp_name ? ` • ${contract.tsp_name}` : '';
        const mdq = Number(contract.max_daily_quantity);
        const capacity = Number.isFinite(mdq)
          ? ` • MDQ ${mdq.toLocaleString()}`
          : '';
        return {
          value: id,
          label: `${
            contract.name || `Contract ${id}`
          } (#${id})${tsp}${capacity}`,
          contract,
        };
      })
      .filter((option): option is ContractPickOption => option !== null)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [activeContractsResp, hasCurrentScenarioContracts, selectedScenario]);

  const pointLabel = React.useCallback(
    (pointId: number | null) => {
      if (pointId == null) return '';
      const point =
        (allPoints as Record<string, any>)?.[String(pointId)] ??
        (allPoints as Record<string, any>)?.[pointId];
      if (!point) return `#${pointId}`;
      const name = point.name || point.loc_name || `Point ${pointId}`;
      const tsp = point.tsp_name ? ` (${point.tsp_name})` : '';
      return `${name}${tsp} #${pointId}`;
    },
    [allPoints],
  );

  const createContractUsage = React.useCallback((): ContractUsageDraft => {
    const rowId = nextContractUsageRowId.current;
    nextContractUsageRowId.current += 1;
    return {
      rowId,
      enabled: true,
      source: 'manual',
      contractId: null,
      capacityScope: 'route',
      receiptPointId: null,
      deliveryPointId: null,
      deliveryVolume: '',
    };
  }, []);

  const onRegionToggle = React.useCallback(
    (region: Region) => {
      const exists = selectedRegions.includes(region);
      const newRegions = exists
        ? selectedRegions.filter((r) => r !== region)
        : [...selectedRegions, region];
      dispatch(
        filtersSlice.filtersChanged({
          filterType: 'regionAttributes',
          name: 'id',
          value: newRegions.map((region) => region.id),
        }),
      );
      setSelectedRegions(newRegions);
    },
    [allPoints, selectedRegions],
  );

  const triggerKPaths = async (kOverride?: number, icpOverride?: number) => {
    const runId = Date.now();
    latestRunId.current = runId;
    try {
      const payload: any = {
        scenario_id: scenarioId,
        gas_date: gasDate,
        k: kOverride ?? kNum.value,
        require_volume: requireVolume,
        resolve: true,
        interconnect_penalty: icpOverride ?? icpNum.value,
        mode: pathMode,
        enforce_region_pathing: enforceRegionPathing,
      };
      if (requireVolume) {
        payload.min_volume = minVol.value;
      }
      if (pathMode === 'diverse') {
        payload.diversify_on = diversifyOn;
        payload.diversify_strategy = 'penalty';
        payload.diversity_penalty = diversityPenalty.value;
      }
      if (avoidConstraints && avoidConstraints.length) {
        payload.avoid_constraints = avoidConstraints.map((x: any) => ({
          constraint_key: x.constraint_key,
          max_rank: x.max_rank,
        }));
      }
      const enabledContractUsages = getEnabledContractUsages(contractUsages);
      Object.assign(
        payload,
        getContractUsageRequestFields(contractUsages, contractUsageMode),
      );

      const allTspIds = tspOptions.map((o) => o.id);

      if (adhocReceiptPointId != null) {
        await syncPathReportAssets({
          scenario_id: scenarioId,
          gas_date: gasDate,
          assets: [
            {
              point_id: adhocReceiptPointId,
              buy_sell: 1,
              gas_date: gasDate,
              base_price: 1,
              max_volume: 1_000_000,
              adder: 0,
              description: 'Ad-hoc Receipt',
              price_source: 'MANUAL',
            },
          ],
        }).unwrap();
        payload.receipt_asset_tokens = [adhocReceiptPointId];
      } else if (rAssetPoints.length > 0) {
        payload.receipt_asset_tokens =
          selectedRegions.length > 0
            ? rAssetPoints.filter((id) => {
                return receiptScopedOptions.some((option) => option.id === id);
              })
            : rAssetPoints;
      }

      if (adhocDeliveryPointId != null) {
        await syncPathReportAssets({
          scenario_id: scenarioId,
          gas_date: gasDate,
          assets: [
            {
              point_id: adhocDeliveryPointId,
              buy_sell: 2,
              gas_date: gasDate,
              base_price: 1,
              max_volume: 1_000_000,
              adder: 0,
              description: 'Ad-hoc Delivery',
              price_source: 'MANUAL',
            },
          ],
        }).unwrap();
        payload.delivery_asset_tokens = [adhocDeliveryPointId];
      } else if (dAssetPoints.length > 0) {
        payload.delivery_asset_tokens =
          selectedRegions.length > 0
            ? dAssetPoints.filter((id) => {
                return deliveryScopedOptions.some((option) => option.id === id);
              })
            : dAssetPoints;
      }

      if (selectedRegions.length > 0) {
        payload.regions = selectedRegions.map((r) => r.id);
      }

      if (enforceRegionPathing) {
        payload.enforce_region_pathing = 'True';
      } else {
        payload.enforce_region_pathing = 'False';
      }

      // Include-only TSP behavior expressed via backend exclusion list.
      if (tspIds.length > 0 && tspIds.length < allTspIds.length) {
        payload.exclude_tsp_ids = allTspIds.filter(
          (id) => !tspIds.includes(id),
        );
      }

      const res = await kPaths(payload).unwrap();
      const items = Array.isArray(res?.paths) ? res.paths : [];
      // Ignore stale responses from earlier runs
      if (latestRunId.current !== runId) return;
      const usageWarnings: ContractUsageWarning[] = Array.isArray(
        res?.contract_usage_warnings,
      )
        ? res.contract_usage_warnings.map(
            (warning: ContractUsageWarning): ContractUsageWarning => ({
              ...warning,
              rowId: enabledContractUsages[warning.row_index]?.rowId ?? null,
            }),
          )
        : [];
      setContractUsageWarnings(usageWarnings);
      if (usageWarnings.length > 0) {
        setIsAdvancedOpen(true);
        setIsContractUsageOpen(true);
        toast(
          `${usageWarnings.length} contract usage ${
            usageWarnings.length === 1 ? 'row was' : 'rows were'
          } skipped.`,
          {
            icon: (
              <WarningTriangleIcon
                style={{
                  width: '1.25rem',
                  height: '1.25rem',
                  color: 'var(--bs-warning)',
                }}
              />
            ),
          },
        );
      }
      dispatch(pfActions.setResults(items));
      // Only show "No paths" if both previous and current results are empty
      if (items.length === 0 && (!kPathsData || kPathsData.length === 0)) {
        toast('No paths found.');
      }
    } catch (e) {
      console.error(e);
      toast.error(`Failed to fetch paths: ${pathServiceErrorMessage(e)}`);
    }
  };

  // Auto-select all receipt and delivery assets by default when assets load,
  // unless specific selections are provided via URL params.
  useEffect(() => {
    const hasRFromUrl = !!(searchParams.get('r_assets') || '').trim();
    const hasDFromUrl = !!(searchParams.get('d_assets') || '').trim();
    const hasAdhocRFromUrl = !!(searchParams.get('adhoc_r_point') || '').trim();
    const hasAdhocDFromUrl = !!(searchParams.get('adhoc_d_point') || '').trim();

    if (!didInitReceipt && receiptPointOptions.length > 0) {
      if (hasAdhocRFromUrl) {
        setDidInitReceipt(true);
      } else if (hasRFromUrl) {
        const rUrlIds = (searchParams.get('r_assets') || '')
          .split(',')
          .filter(Boolean)
          .map(Number);
        const validR = rUrlIds.filter((id) =>
          receiptPointOptions.some((o) => o.id === id),
        );
        setRAssetPoints(validR);
        setDidInitReceipt(true);
      } else if (rAssetPoints.length === 0) {
        setRAssetPoints(receiptPointOptions.map((o) => o.id));
        setDidInitReceipt(true);
      }
    }

    if (!didInitDelivery && deliveryPointOptions.length > 0) {
      if (hasAdhocDFromUrl) {
        setDidInitDelivery(true);
      } else if (hasDFromUrl) {
        const dUrlIds = (searchParams.get('d_assets') || '')
          .split(',')
          .filter(Boolean)
          .map(Number);
        const validD = dUrlIds.filter((id) =>
          deliveryPointOptions.some((o) => o.id === id),
        );
        setDAssetPoints(validD);
        setDidInitDelivery(true);
      } else if (dAssetPoints.length === 0) {
        setDAssetPoints(deliveryPointOptions.map((o) => o.id));
        setDidInitDelivery(true);
      }
    }
  }, [
    receiptPointOptions,
    deliveryPointOptions,
    searchParams,
    didInitReceipt,
    didInitDelivery,
    rAssetPoints.length,
    dAssetPoints.length,
  ]);

  // Reset init flags when scenario or gas date changes so auto-select can run once for new context
  useEffect(() => {
    setDidInitReceipt(false);
    setDidInitDelivery(false);
    setDidInitTsp(false);
    setContractUsages([]);
    setContractUsageWarnings([]);
    setIsContractUsageManagerOpen(false);
    nextContractUsageRowId.current = 1;
  }, [scenarioId, gasDate]);

  // Auto-select all TSPs by default when TSPs load, unless specific selections are provided via URL params.
  useEffect(() => {
    const hasTspFromUrl = !!(searchParams.get('tsp_ids') || '').trim();
    if (!didInitTsp && tspOptions.length > 0) {
      if (hasTspFromUrl) {
        const tspUrlIds = (searchParams.get('tsp_ids') || '')
          .split(',')
          .filter(Boolean)
          .map(Number);
        const validTsp = tspUrlIds.filter((id) =>
          tspOptions.some((o) => o.id === id),
        );
        setTspIds(validTsp);
        setDidInitTsp(true);
      } else if (tspIds.length === 0) {
        setTspIds(tspOptions.map((o) => o.id));
        setDidInitTsp(true);
      }
    }
  }, [tspOptions, searchParams, didInitTsp, tspIds.length]);

  // Inject usable receipt/delivery points into plotter markets for MapMarkets
  useEffect(() => {
    const allReceiptIds = receiptPointOptions.map((o) => o.id);
    const allDeliveryIds = deliveryPointOptions.map((o) => o.id);

    // Include-only semantics: empty selection means all available assets.
    const usableReceipts =
      rAssetPoints.length > 0 ? rAssetPoints.slice() : allReceiptIds;
    const usableDeliveries =
      dAssetPoints.length > 0 ? dAssetPoints.slice() : allDeliveryIds;

    // If both empty, clear markets
    if (usableReceipts.length === 0 && usableDeliveries.length === 0) {
      dispatch(plotterActions.setMarkets([]));
      return;
    }

    const markets = [
      ...usableReceipts.map((pid) => ({
        id: 100000 + pid,
        description: 'PathFinder Receipt',
        price: 0,
        point: pid,
      })),
      ...usableDeliveries.map((pid) => ({
        id: 200000 + pid,
        description: 'PathFinder Delivery',
        price: 0,
        point: pid,
      })),
    ];

    dispatch(plotterActions.setMarkets(markets));
  }, [
    dispatch,
    receiptPointOptions,
    deliveryPointOptions,
    rAssetPoints,
    dAssetPoints,
  ]);

  const filteredTspOptions = React.useMemo(() => {
    const q = tspFilter.toLowerCase();
    return tspOptions.filter((opt) => opt.label.toLowerCase().includes(q));
  }, [tspOptions, tspFilter]);

  const selectedTspScope = React.useMemo(() => {
    const allTspIds = tspOptions.map((opt) => opt.id);
    // const regionSet = new Set<number>(
    //   getSelectedTspIdsForRegions(selectedRegions, allTspIds),
    // );

    const tspSet = new Set<number>(tspIds);
    const tspLooksUnconstrained =
      tspIds.length === 0 || tspIds.length === allTspIds.length;

    // If a region is selected but tspIds are currently unconstrained,
    // preserve region intent for asset scoping.
    const selectedSet = tspSet;

    const effectivelyAllSelected =
      selectedRegions.length === 0 && tspLooksUnconstrained;

    return {
      selectedSet,
      effectivelyAllSelected,
    };
  }, [selectedRegions, tspIds, tspOptions]);

  const isPointInSelectedTspScope = React.useCallback(
    (pointId: number) => {
      if (
        !allPoints ||
        Object.keys(allPoints as Record<string, any>).length === 0
      ) {
        return true;
      }
      const point =
        (allPoints as any)?.[String(pointId)] ?? (allPoints as any)?.[pointId];
      if (selectedTspScope.effectivelyAllSelected) {
        return true;
      }
      const pointTspId = Number(point?.tsp_id);
      if (!Number.isFinite(pointTspId)) {
        return false;
      }
      return selectedTspScope.selectedSet.has(pointTspId);
    },
    [allPoints, selectedTspScope],
  );

  const isPointInSelectedRegionsScope = React.useCallback(
    (pointId: number) => {
      if (selectedRegions.length === 0) {
        return true;
      }
      if (
        !allPoints ||
        Object.keys(allPoints as Record<string, any>).length === 0
      ) {
        return true;
      }
      const point =
        (allPoints as any)?.[String(pointId)] ?? (allPoints as any)?.[pointId];
      return selectedRegions.some((region) => {
        return (
          region.tsps.some((tsp) => tsp.id === point.tsp_id) ||
          region.zones.some(
            (zone) =>
              zone.id === point.receipt_zone || zone.id === point.delivery_zone,
          )
        );
      });
    },
    [selectedRegions],
  );

  const receiptScopedOptions = React.useMemo(() => {
    const selectedSet = new Set(rAssetPoints);
    return receiptPointOptions.filter((opt) => {
      if (!isPointInSelectedRegionsScope(opt.id)) {
        return false;
      }
      if (showOutsideTspAssets) {
        return true;
      }
      if (selectedSet.has(opt.id)) {
        return true;
      }
      return isPointInSelectedTspScope(opt.id);
    });
  }, [
    receiptPointOptions,
    rAssetPoints,
    showOutsideTspAssets,
    isPointInSelectedTspScope,
  ]);

  const deliveryScopedOptions = React.useMemo(() => {
    const selectedSet = new Set(dAssetPoints);
    return deliveryPointOptions.filter((opt) => {
      if (!isPointInSelectedRegionsScope(opt.id)) {
        return false;
      }
      if (showOutsideTspAssets) {
        return true;
      }
      if (selectedSet.has(opt.id)) {
        return true;
      }
      return isPointInSelectedTspScope(opt.id);
    });
  }, [
    deliveryPointOptions,
    dAssetPoints,
    showOutsideTspAssets,
    isPointInSelectedTspScope,
  ]);

  const filteredReceiptOptions = React.useMemo(() => {
    const q = rFilter.toLowerCase();
    return receiptScopedOptions.filter((opt) =>
      opt.label.toLowerCase().includes(q),
    );
  }, [receiptScopedOptions, rFilter]);

  const filteredDeliveryOptions = React.useMemo(() => {
    const q = dFilter.toLowerCase();
    return deliveryScopedOptions.filter((opt) =>
      opt.label.toLowerCase().includes(q),
    );
  }, [deliveryScopedOptions, dFilter]);

  const receiptQuickPickOptions = React.useMemo<AssetPickOption[]>(() => {
    const existingOptions = receiptScopedOptions.map((opt) => ({
      value: String(opt.id),
      label: opt.label,
    }));
    return [
      ...existingOptions,
      { value: '__add_new__', label: '+ Add new receipt point' },
    ];
  }, [receiptScopedOptions]);

  const deliveryQuickPickOptions = React.useMemo<AssetPickOption[]>(() => {
    const existingOptions = deliveryScopedOptions.map((opt) => ({
      value: String(opt.id),
      label: opt.label,
    }));
    return [
      ...existingOptions,
      { value: '__add_new__', label: '+ Add new delivery point' },
    ];
  }, [deliveryScopedOptions]);

  const adhocReceiptPointDisplay = React.useMemo<string>(() => {
    if (adhocReceiptPointId == null) {
      return '';
    }
    const point =
      (allPoints as Record<string, any>)?.[String(adhocReceiptPointId)] ??
      (allPoints as Record<string, any>)?.[adhocReceiptPointId];
    if (!point) {
      return `#${adhocReceiptPointId}`;
    }
    const name =
      point?.name || point?.loc_name || `Point ${adhocReceiptPointId}`;
    const tsp = point?.tsp_name ? ` (${point.tsp_name})` : '';
    return `${name}${tsp} #${adhocReceiptPointId}`;
  }, [adhocReceiptPointId, allPoints]);

  const receiptQuickPickValue = React.useMemo<AssetPickOption | null>(() => {
    if (adhocReceiptPointId != null) {
      return {
        value: `adhoc:${adhocReceiptPointId}`,
        label: `Ad-hoc: ${adhocReceiptPointDisplay}`,
      };
    }
    if (rAssetPoints.length !== 1) {
      return null;
    }
    const selectedId = rAssetPoints[0];
    const selected = receiptPointOptions.find((opt) => opt.id === selectedId);
    return selected
      ? { value: String(selected.id), label: selected.label }
      : { value: String(selectedId), label: `#${selectedId}` };
  }, [
    rAssetPoints,
    receiptPointOptions,
    adhocReceiptPointId,
    adhocReceiptPointDisplay,
  ]);

  const adhocDeliveryPointDisplay = React.useMemo<string>(() => {
    if (adhocDeliveryPointId == null) {
      return '';
    }
    const point =
      (allPoints as Record<string, any>)?.[String(adhocDeliveryPointId)] ??
      (allPoints as Record<string, any>)?.[adhocDeliveryPointId];
    if (!point) {
      return `#${adhocDeliveryPointId}`;
    }
    const name =
      point?.name || point?.loc_name || `Point ${adhocDeliveryPointId}`;
    const tsp = point?.tsp_name ? ` (${point.tsp_name})` : '';
    return `${name}${tsp} #${adhocDeliveryPointId}`;
  }, [adhocDeliveryPointId, allPoints]);

  const deliveryQuickPickValue = React.useMemo<AssetPickOption | null>(() => {
    if (adhocDeliveryPointId != null) {
      return {
        value: `adhoc:${adhocDeliveryPointId}`,
        label: `Ad-hoc: ${adhocDeliveryPointDisplay}`,
      };
    }
    if (dAssetPoints.length !== 1) {
      return null;
    }
    const selectedId = dAssetPoints[0];
    const selected = deliveryPointOptions.find((opt) => opt.id === selectedId);
    return selected
      ? { value: String(selected.id), label: selected.label }
      : { value: String(selectedId), label: `#${selectedId}` };
  }, [
    dAssetPoints,
    deliveryPointOptions,
    adhocDeliveryPointId,
    adhocDeliveryPointDisplay,
  ]);

  const handleFindPaths = React.useCallback(() => {
    const invalidUsageIndex =
      findInvalidEnabledContractUsageIndex(contractUsages);
    if (invalidUsageIndex >= 0) {
      const error = contractUsageError(contractUsages[invalidUsageIndex]);
      setIsAdvancedOpen(true);
      setIsContractUsageOpen(true);
      setIsContractUsageManagerOpen(true);
      toast.error(`Contract usage ${invalidUsageIndex + 1}: ${error}.`);
      return;
    }

    const kFinal = kNum.normalize();
    const icpFinal = icpNum.normalize();
    const params: any = {
      k: String(kFinal),
      vol: requireVolume ? '1' : '0',
      icp: String(icpFinal),
    };
    if (requireVolume) params.minv = String(minVol.normalize());
    if (gasDate) params.date = gasDate; // was activeGasDate
    if (adhocReceiptPointId != null) {
      params.adhoc_r_point = String(adhocReceiptPointId);
    } else if (rAssetPoints.length > 0) {
      params.r_assets = rAssetPoints.join(',');
    }
    if (adhocDeliveryPointId != null) {
      params.adhoc_d_point = String(adhocDeliveryPointId);
    } else if (dAssetPoints.length > 0) {
      params.d_assets = dAssetPoints.join(',');
    }
    if (tspIds.length > 0) params.tsp_ids = tspIds.join(',');
    params.mode = pathMode;
    if (pathMode === 'diverse') {
      params.div_on = diversifyOn;
      params.div_penalty = String(diversityPenalty.normalize());
    }
    setSearchParams(params);
    triggerKPaths(kFinal, icpFinal);
  }, [
    kNum,
    requireVolume,
    minVol,
    gasDate,
    adhocReceiptPointId,
    rAssetPoints,
    dAssetPoints,
    adhocDeliveryPointId,
    tspIds,
    pathMode,
    diversifyOn,
    diversityPenalty,
    contractUsages,
    setSearchParams,
    triggerKPaths,
    icpNum,
  ]);

  useEffect(() => {
    const avoidConstraintsKey = JSON.stringify(avoidConstraints);
    const constraintsChanged =
      previousAvoidConstraintsKey.current !== avoidConstraintsKey;
    previousAvoidConstraintsKey.current = avoidConstraintsKey;

    if (autoRefreshAvoidConstraints && constraintsChanged) {
      handleFindPaths();
    }
  }, [autoRefreshAvoidConstraints, avoidConstraints, handleFindPaths]);

  const handleAssetQuickPickChange = React.useCallback(
    (
      selected: SingleValue<AssetPickOption>,
      setAssetPoints: React.Dispatch<React.SetStateAction<number[]>>,
      setAdhocPointId: React.Dispatch<React.SetStateAction<number | null>>,
      setIsAddingNew: React.Dispatch<React.SetStateAction<boolean>>,
    ) => {
      if (!selected) {
        setAssetPoints([]);
        setAdhocPointId(null);
        setIsAddingNew(false);
        return;
      }

      if (selected.value === '__add_new__') {
        setAssetPoints([]);
        setAdhocPointId(null);
        setIsAddingNew(true);
        return;
      }

      if (selected.value.startsWith('adhoc:')) {
        const parsed = Number(selected.value.split(':')[1]);
        if (Number.isFinite(parsed)) {
          setAssetPoints([]);
          setAdhocPointId(parsed);
        }
        setIsAddingNew(false);
        return;
      }

      const existingPointId = Number(selected.value);
      if (Number.isFinite(existingPointId)) {
        setAssetPoints([existingPointId]);
        setAdhocPointId(null);
      }
      setIsAddingNew(false);
    },
    [],
  );

  const renderAssetSection = ({
    key,
    title,
    kind,
    quickPickValue,
    quickPickOptions,
    setAssetPoints,
    assetPoints,
    setAdhocPointId,
    adhocPointId,
    adhocPointDisplay,
    isAddingNew,
    setIsAddingNew,
    isAdvancedOpen,
    setIsAdvancedOpen,
    filter,
    setFilter,
    filteredOptions,
  }: {
    key: 'receipt' | 'delivery';
    title: string;
    kind: 'receipt' | 'delivery';
    quickPickValue: AssetPickOption | null;
    quickPickOptions: AssetPickOption[];
    setAssetPoints: React.Dispatch<React.SetStateAction<number[]>>;
    assetPoints: number[];
    setAdhocPointId: React.Dispatch<React.SetStateAction<number | null>>;
    adhocPointId: number | null;
    adhocPointDisplay: string;
    isAddingNew: boolean;
    setIsAddingNew: React.Dispatch<React.SetStateAction<boolean>>;
    isAdvancedOpen: boolean;
    setIsAdvancedOpen: React.Dispatch<React.SetStateAction<boolean>>;
    filter: string;
    setFilter: React.Dispatch<React.SetStateAction<string>>;
    filteredOptions: Array<{ id: number; label: string }>;
  }) => {
    const [appTheme] = useTheme();
    const scopedOptionsForKind =
      kind === 'receipt' ? receiptScopedOptions : deliveryScopedOptions;
    const scopedPointIds = new Set(scopedOptionsForKind.map((opt) => opt.id));
    const selectedInScopeCount = assetPoints.filter((id) =>
      scopedPointIds.has(id),
    ).length;
    const totalInScopeCount = scopedOptionsForKind.length;
    const scopeLabel =
      selectedRegions.length === 0
        ? 'all regions'
        : selectedRegions.length === 1
        ? `${selectedRegions[0].name} region`
        : `${selectedRegions.length} regions`;
    const kindPlural = kind === 'receipt' ? 'receipts' : 'deliveries';

    const selectionSummary =
      adhocPointId != null
        ? `Using selected ad-hoc ${kind} for this run.`
        : totalInScopeCount === 0
        ? `No ${kindPlural} available in ${scopeLabel}.`
        : selectedInScopeCount === totalInScopeCount
        ? `All ${kindPlural} selected in ${scopeLabel} (${selectedInScopeCount}/${totalInScopeCount}).`
        : selectedInScopeCount === 0
        ? `No ${kindPlural} selected in ${scopeLabel}.`
        : `${selectedInScopeCount} of ${totalInScopeCount} ${kindPlural} selected in ${scopeLabel}.`;

    return (
      <div key={key} className="d-flex flex-column">
        <button
          type="button"
          className="btn w-100 p-0 border-0 d-flex align-items-center justify-content-between border-bottom pb-1"
          onClick={() => setIsAdvancedOpen((prev) => !prev)}
          title={
            isAdvancedOpen
              ? `Collapse ${title} advanced`
              : `Expand ${title} advanced`
          }
        >
          <span className="h5 mb-0">{title}</span>
          {isAdvancedOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>
        <div className="d-flex flex-column gap-1 mt-2">
          <Select
            classNamePrefix="pf-quick-pick"
            value={quickPickValue}
            options={quickPickOptions}
            onChange={(selected: SingleValue<AssetPickOption>) =>
              handleAssetQuickPickChange(
                selected,
                setAssetPoints,
                setAdhocPointId,
                setIsAddingNew,
              )
            }
            isClearable
            isSearchable
            placeholder={`Type to find a ${kind} asset...`}
            noOptionsMessage={() => `No matching ${kind} assets`}
            theme={(theme) => ({
              ...theme,
              borderRadius: 4,
              colors: {
                ...theme.colors,
                ...selectStyles[appTheme],
              },
            })}
          />
          {isAddingNew && (
            <div className="d-flex gap-2 mt-2">
              <ReduxPointSearch
                value={adhocPointDisplay}
                placeholder={`Search ${kind} point...`}
                className="form-control"
                handler={({ id }) => {
                  const pointId = Number(id);
                  if (Number.isFinite(pointId)) {
                    setAdhocPointId(pointId);
                    setAssetPoints([]);
                    setIsAddingNew(false);
                  }
                }}
              />
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => {
                  setAdhocPointId(null);
                  setIsAddingNew(false);
                }}
              >
                Cancel
              </button>
            </div>
          )}
        </div>

        {isAdvancedOpen && (
          <div className="mt-2 border rounded p-2">
            <div className="form-check mb-2">
              <input
                className="form-check-input"
                type="checkbox"
                id={`showOutsideTspAssets-${key}`}
                checked={showOutsideTspAssets}
                onChange={(e) => setShowOutsideTspAssets(e.target.checked)}
                title="Include assets that are outside your selected TSP scope"
              />
              <label
                className="form-check-label"
                htmlFor={`showOutsideTspAssets-${key}`}
              >
                Show assets outside selected TSPs
              </label>
            </div>
            <input
              type="text"
              className="form-control form-control-sm mb-2"
              placeholder={`Filter ${kind}s...`}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
            <div className="d-flex gap-2 mb-2">
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() =>
                  setAssetPoints((prev) =>
                    Array.from(
                      new Set([...prev, ...filteredOptions.map((o) => o.id)]),
                    ),
                  )
                }
              >
                Select All
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() =>
                  setAssetPoints((prev) =>
                    prev.filter(
                      (id) => !filteredOptions.some((o) => o.id === id),
                    ),
                  )
                }
              >
                Clear
              </button>
            </div>
            <div className="d-flex justify-content-end mb-2">
              <span className="badge bg-light text-dark">
                Selected:{' '}
                {
                  assetPoints.filter((id) =>
                    filteredOptions.some((o) => o.id === id),
                  ).length
                }
                /{filteredOptions.length}
              </span>
            </div>
            <div
              className="border rounded p-2"
              style={{ maxHeight: 200, overflowY: 'auto' }}
            >
              {filteredOptions.map((opt) => {
                const inputId = `${kind}-asset-${opt.id}-${scenarioId}`;
                return (
                  <div className="form-check" key={inputId}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={inputId}
                      checked={assetPoints.includes(opt.id)}
                      onChange={(e) => {
                        setAssetPoints((prev) =>
                          e.target.checked
                            ? [...prev, opt.id]
                            : prev.filter((x) => x !== opt.id),
                        );
                      }}
                    />
                    <label className="form-check-label" htmlFor={inputId}>
                      {opt.label}
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <small className="text-muted mt-1">{selectionSummary}</small>
      </div>
    );
  };

  const renderDisclosureHeader = (
    title: string,
    isOpen: boolean,
    setOpen: React.Dispatch<React.SetStateAction<boolean>>,
    leadingIcon?: React.ReactNode,
  ) => (
    <button
      type="button"
      className="btn w-100 p-0 border-0 d-flex align-items-center justify-content-between border-bottom pb-1"
      onClick={() => setOpen((prev) => !prev)}
      title={isOpen ? `Collapse ${title}` : `Expand ${title}`}
    >
      <span className="h6 mb-0 text-start d-flex align-items-center gap-2">
        {leadingIcon}
        {title}
      </span>
      {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
    </button>
  );

  const assetSections = [
    {
      key: 'receipt' as const,
      title: 'Receipt Assets',
      kind: 'receipt' as const,
      quickPickValue: receiptQuickPickValue,
      quickPickOptions: receiptQuickPickOptions,
      setAssetPoints: setRAssetPoints,
      assetPoints: rAssetPoints,
      setAdhocPointId: setAdhocReceiptPointId,
      adhocPointId: adhocReceiptPointId,
      adhocPointDisplay: adhocReceiptPointDisplay,
      isAddingNew: isAddingReceiptNew,
      setIsAddingNew: setIsAddingReceiptNew,
      isAdvancedOpen: isReceiptAdvancedOpen,
      setIsAdvancedOpen: setIsReceiptAdvancedOpen,
      filter: rFilter,
      setFilter: setRFilter,
      filteredOptions: filteredReceiptOptions,
    },
    {
      key: 'delivery' as const,
      title: 'Delivery Assets',
      kind: 'delivery' as const,
      quickPickValue: deliveryQuickPickValue,
      quickPickOptions: deliveryQuickPickOptions,
      setAssetPoints: setDAssetPoints,
      assetPoints: dAssetPoints,
      setAdhocPointId: setAdhocDeliveryPointId,
      adhocPointId: adhocDeliveryPointId,
      adhocPointDisplay: adhocDeliveryPointDisplay,
      isAddingNew: isAddingDeliveryNew,
      setIsAddingNew: setIsAddingDeliveryNew,
      isAdvancedOpen: isDeliveryAdvancedOpen,
      setIsAdvancedOpen: setIsDeliveryAdvancedOpen,
      filter: dFilter,
      setFilter: setDFilter,
      filteredOptions: filteredDeliveryOptions,
    },
  ];

  return (
    <div className="mb-3 d-flex flex-column gap-3">
      {/* Region-first quick start */}
      <div className="d-flex flex-column">
        <div className="d-flex align-items-center justify-content-between">
          <label className="form-label mb-0">Region</label>
          <div
            className="btn-group btn-group-sm"
            role="group"
            aria-label="Region selector"
          >
            {regions
              ?.slice()
              .sort((a, b) => a.id - b.id)
              .map((region) => (
                <button
                  key={region.id.toString()}
                  type="button"
                  className={`btn ${
                    selectedRegions.includes(region)
                      ? 'btn-primary'
                      : 'btn-outline-primary'
                  }`}
                  onClick={() => onRegionToggle(region)}
                >
                  {region.name}
                </button>
              ))}
          </div>
        </div>
      </div>

      {assetSections.map((section) => renderAssetSection(section))}

      <div className="d-flex flex-column">
        {renderDisclosureHeader(
          'Advanced Controls',
          isAdvancedOpen,
          setIsAdvancedOpen,
          <SlidersHorizontal size={18} />,
        )}
      </div>

      {isAdvancedOpen && (
        <div className="d-flex flex-column gap-3 p-2">
          <div className="d-flex flex-column">
            {renderDisclosureHeader(
              'Pathing options',
              isPathingOptionsOpen,
              setIsPathingOptionsOpen,
            )}
            {isPathingOptionsOpen && (
              <label className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={enforceRegionPathing}
                  onChange={(event) =>
                    setEnforceRegionPathing(event.target.checked)
                  }
                />
                <span className="form-check-label">Enforce region pathing</span>
              </label>
            )}
          </div>
          <div className="d-flex flex-column">
            {renderDisclosureHeader(
              'Advanced TSP Selector',
              isTspAdvancedOpen,
              setIsTspAdvancedOpen,
            )}
            {isTspAdvancedOpen && (
              <div className="mt-2 border rounded p-2">
                <input
                  type="text"
                  className="form-control form-control-sm mb-2"
                  placeholder="Filter TSPs..."
                  value={tspFilter}
                  onChange={(e) => setTspFilter(e.target.value)}
                />
                <div className="d-flex gap-2 mb-2">
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setTspIds(tspOptions.map((o) => o.id))}
                  >
                    Select All
                  </button>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setTspIds([])}
                  >
                    Clear
                  </button>
                </div>
                <div
                  className="border rounded p-2"
                  style={{ maxHeight: 160, overflowY: 'auto' }}
                >
                  {filteredTspOptions.map((opt) => (
                    <div className="form-check" key={`tsp-${opt.id}`}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`tsp-${opt.id}`}
                        checked={tspIds.includes(opt.id)}
                        onChange={(e) => {
                          setTspIds((prev) =>
                            e.target.checked
                              ? [...prev, opt.id]
                              : prev.filter((x) => x !== opt.id),
                          );
                        }}
                      />
                      <label
                        className="form-check-label"
                        htmlFor={`tsp-${opt.id}`}
                      >
                        {opt.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="d-flex flex-column">
            {renderDisclosureHeader(
              'Avoid Constraints',
              isConstraintAdvancedOpen,
              setIsConstraintAdvancedOpen,
            )}
            {isConstraintAdvancedOpen && (
              <>
                <label className="form-check form-switch mt-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={autoRefreshAvoidConstraints}
                    onChange={(event) =>
                      setAutoRefreshAvoidConstraints(event.target.checked)
                    }
                  />
                  <span className="form-check-label">
                    Automatically refresh after changing avoid constraints
                  </span>
                </label>
                <div className="d-flex align-items-center justify-content-between mb-1 mt-2">
                  {avoidConstraints?.length ? (
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() =>
                        dispatch(pfActions.clearAvoidConstraints())
                      }
                    >
                      Clear
                    </button>
                  ) : null}
                </div>
                {!avoidConstraints || avoidConstraints.length === 0 ? (
                  <small className="text-muted">
                    Use the + icon in constraint tables to add.
                  </small>
                ) : (
                  <div
                    className="border rounded p-2"
                    style={{ maxHeight: 140, overflowY: 'auto' }}
                  >
                    {avoidConstraints.map((ac: any, idx: number) => (
                      <div
                        key={`${ac.constraint_key}:${ac.mode ?? 'up_to'}:${
                          ac.max_rank
                        }:${idx}`}
                        className="d-flex align-items-center justify-content-between py-1 border-bottom"
                      >
                        <div
                          className="small me-2"
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <span className="badge bg-light text-dark me-1">
                            {(ac.mode ?? 'up_to') === 'all'
                              ? 'ALL'
                              : `≤${ac.max_rank}`}
                          </span>
                          <span title={ac.constraint_key}>
                            {ac.label ?? ac.constraint_key}
                          </span>
                        </div>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() =>
                            dispatch(
                              pfActions.removeAvoidConstraint({
                                constraint_key: ac.constraint_key,
                                max_rank: ac.max_rank,
                                mode: ac.mode ?? 'up_to',
                              }),
                            )
                          }
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          <div className="d-flex flex-column">
            {renderDisclosureHeader(
              'Contract Usage',
              isContractUsageOpen,
              setIsContractUsageOpen,
            )}
            {isContractUsageOpen && (
              <div className="mt-2 d-flex flex-column gap-2">
                <small className="text-muted">
                  Choose whether existing commitments restrict suggestions or
                  are reported after the search.
                </small>

                <fieldset>
                  <legend className="form-label small mb-1">Behavior</legend>
                  <div
                    className="btn-group btn-group-sm w-100"
                    role="group"
                    aria-label="Contract Usage behavior"
                  >
                    <input
                      className="btn-check"
                      type="radio"
                      name="contract-usage-mode"
                      id="contract-usage-mode-enforce"
                      checked={contractUsageMode === 'enforce'}
                      onChange={() => setContractUsageMode('enforce')}
                    />
                    <label
                      className="btn btn-outline-primary"
                      htmlFor="contract-usage-mode-enforce"
                    >
                      Restrict Suggestions
                    </label>

                    <input
                      className="btn-check"
                      type="radio"
                      name="contract-usage-mode"
                      id="contract-usage-mode-report"
                      checked={contractUsageMode === 'report'}
                      onChange={() => setContractUsageMode('report')}
                    />
                    <label
                      className="btn btn-outline-primary"
                      htmlFor="contract-usage-mode-report"
                    >
                      Report Availability Only
                    </label>
                  </div>
                </fieldset>

                <small className="text-muted">
                  {contractUsageMode === 'enforce'
                    ? 'Find Paths uses remaining contract capacity when selecting contracts and suggestions.'
                    : 'Suggestions remain unchanged. Results show the volume available after Contract Usage.'}
                </small>

                <div className="d-flex align-items-center justify-content-between">
                  <span className="badge bg-light text-dark">
                    {getEnabledContractUsages(contractUsages).length} of{' '}
                    {contractUsages.length} active
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => setIsContractUsageManagerOpen(true)}
                  >
                    Manage Usages
                  </button>
                </div>

                {contractUsages.length === 0 && (
                  <small className="text-muted">
                    No contract usage will be included in this search.
                  </small>
                )}

                {contractUsageWarnings.length > 0 && (
                  <div className="alert alert-warning py-2 px-3 mb-0">
                    <small>
                      {contractUsageWarnings.length}{' '}
                      {contractUsageWarnings.length === 1
                        ? 'usage warning needs'
                        : 'usage warnings need'}{' '}
                      attention. Open Manage Usages for details.
                    </small>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="d-flex flex-column">
            {renderDisclosureHeader(
              'Path Search Settings',
              isPathSettingsOpen,
              setIsPathSettingsOpen,
            )}
            {isPathSettingsOpen && (
              <>
                <div className="d-flex flex-column mt-2">
                  <label className="form-label" htmlFor="path-count">
                    Number of Paths
                  </label>
                  <input
                    type="number"
                    className="form-control w-100"
                    min={1}
                    max={100}
                    step={1}
                    id="path-count"
                    {...kNum.bind}
                  />
                </div>
                <div className="d-flex flex-column">
                  <label className="form-label">
                    Interconnect Penalty ($/dth)
                  </label>
                  <input
                    type="number"
                    className="form-control w-100"
                    step={0.01}
                    min={0}
                    {...icpNum.bind}
                  />
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="requireVolume"
                    checked={requireVolume}
                    onChange={(e) => setRequireVolume(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="requireVolume">
                    Require Volume
                  </label>
                </div>
                <div className="d-flex flex-column">
                  <label className="form-label">Minimum Volume (dth)</label>
                  <input
                    type="number"
                    className="form-control w-100"
                    min={0}
                    step={1}
                    {...minVol.bind}
                    disabled={!requireVolume}
                  />
                </div>

                <div className="d-flex flex-column">
                  <label className="form-label">Mode</label>
                  <div
                    className="btn-group"
                    role="group"
                    aria-label="Path mode"
                  >
                    <button
                      type="button"
                      className={`btn ${
                        pathMode === 'diverse'
                          ? 'btn-primary'
                          : 'btn-outline-primary'
                      }`}
                      onClick={() => setPathMode('diverse')}
                    >
                      Diverse
                    </button>
                    <button
                      type="button"
                      className={`btn ${
                        pathMode === 'sets'
                          ? 'btn-primary'
                          : 'btn-outline-primary'
                      }`}
                      onClick={() => setPathMode('sets')}
                    >
                      Specific
                    </button>
                  </div>
                </div>
                {pathMode === 'diverse' && (
                  <div className="d-flex flex-column gap-2 mt-2">
                    <div className="d-flex justify-content-between">
                      <label className="form-label mb-1">
                        Diversify Assets On
                      </label>
                      <div
                        className="float-right btn-group btn-group-sm"
                        role="group"
                        aria-label="Diversify on"
                      >
                        <button
                          type="button"
                          className={`btn ${
                            diversifyOn === 'both'
                              ? 'btn-primary'
                              : 'btn-outline-primary'
                          }`}
                          onClick={() => setDiversifyOn('both')}
                        >
                          Both
                        </button>
                        <button
                          type="button"
                          className={`btn ${
                            diversifyOn === 'receipt'
                              ? 'btn-primary'
                              : 'btn-outline-primary'
                          }`}
                          onClick={() => setDiversifyOn('receipt')}
                        >
                          Receipt
                        </button>
                        <button
                          type="button"
                          className={`btn ${
                            diversifyOn === 'delivery'
                              ? 'btn-primary'
                              : 'btn-outline-primary'
                          }`}
                          onClick={() => setDiversifyOn('delivery')}
                        >
                          Delivery
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="form-label">
                        Diversity Penalty ($/dth)
                      </label>
                      <input
                        type="number"
                        className="form-control w-100"
                        min={0}
                        step={0.01}
                        {...diversityPenalty.bind}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      <ContractUsageManagerModal
        isOpen={isContractUsageManagerOpen}
        usages={contractUsages}
        warnings={contractUsageWarnings}
        contractOptions={contractOptions}
        contractsLoading={contractsLoading}
        defaultNominationGasDate={gasDate}
        pointLabel={pointLabel}
        createUsage={createContractUsage}
        onApply={(usages) => {
          setContractUsages(usages);
          setContractUsageWarnings([]);
          setIsContractUsageManagerOpen(false);
        }}
        onClose={() => setIsContractUsageManagerOpen(false)}
      />

      <div className="position-sticky" style={{ bottom: 16, zIndex: 1030 }}>
        <button
          className="btn btn-success w-100 shadow"
          onClick={handleFindPaths}
          disabled={kPathsLoading}
        >
          {kPathsLoading ? 'Searching…' : 'Find Paths'}
        </button>
      </div>
    </div>
  );
};
